import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';

interface SidebarProps {
  isOpen: boolean;
  onToggle: () => void;
}

// Icon Components
const DashboardIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
  </svg>
);

const NotificationIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
  </svg>
);

const BookingsIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);

const CustomersIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
  </svg>
);

const FleetIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
  </svg>
);

const PricingIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const WebsiteIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
  </svg>
);

const AnalyticsIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
  </svg>
);

const UsersIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
  </svg>
);

const HealthIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
  </svg>
);

const AuditIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5h6m-6 4h6m-6 4h4m5 7H6a2 2 0 01-2-2V6a2 2 0 012-2h3.5a2 2 0 001.4-.6l.7-.8a2 2 0 011.5-.6h1.8a2 2 0 011.5.6l.7.8a2 2 0 001.4.6H18a2 2 0 012 2v12a2 2 0 01-2 2z" />
  </svg>
);

const ChevronIcon = ({ open }: { open: boolean }) => (
  <svg className={`w-4 h-4 transition-transform duration-200 ${open ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
  </svg>
);

interface MenuItem {
  path: string;
  label: string;
  permission: string;
}

interface MenuGroup {
  label: string;
  icon: React.FC;
  permission: string;
  items: MenuItem[];
}

interface TopLevelItem {
  path: string;
  label: string;
  icon: React.FC;
  permission: string;
  requiredRole?: string;
  badge?: 'notifications';
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onToggle }) => {
  const location = useLocation();
  const { hasPermission, hasRole } = useAuth();
  const { unreadCount } = useNotifications();

  // Track which groups are expanded (click)
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(() => {
    const path = location.pathname;
    const initial: Record<string, boolean> = {};
    if (['/discounts', '/seasonal-pricing', '/price-management'].includes(path)) initial['pricing'] = true;
    if (['/bookings', '/customers'].includes(path)) initial['operations'] = true;
    if (['/fleet', '/maintenance', '/issues', '/compliance'].includes(path)) initial['fleet'] = true;
    if (['/pages', '/blog'].includes(path)) initial['content'] = true;
    if (['/website-analytics', '/seo'].includes(path)) initial['analytics'] = true;
    return initial;
  });

  // Track which group is hovered (for flyout)
  const [hoveredGroup, setHoveredGroup] = useState<string | null>(null);
  const [flyoutPosition, setFlyoutPosition] = useState<{ top: number } | null>(null);
  const hoverTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  const handleGroupMouseEnter = (key: string, event: React.MouseEvent<HTMLDivElement>) => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    const rect = event.currentTarget.getBoundingClientRect();
    // Smart positioning: keep flyout within viewport
    const flyoutHeight = 200; // approximate
    const maxTop = window.innerHeight - flyoutHeight - 20;
    const top = Math.min(rect.top, maxTop);
    setFlyoutPosition({ top: Math.max(10, top) });
    setHoveredGroup(key);
  };

  const handleGroupMouseLeave = () => {
    hoverTimeoutRef.current = setTimeout(() => {
      setHoveredGroup(null);
      setFlyoutPosition(null);
    }, 150);
  };

  const handleFlyoutMouseEnter = () => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
  };

  const handleFlyoutMouseLeave = () => {
    setHoveredGroup(null);
    setFlyoutPosition(null);
  };

  const toggleGroup = (key: string) => {
    setExpandedGroups(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Top-level items (no grouping)
  const topItems: TopLevelItem[] = [
    { path: '/dashboard', label: 'Dashboard', icon: DashboardIcon, permission: 'view_dashboard' },
    { path: '/notifications', label: 'Notifications', icon: NotificationIcon, permission: 'view_dashboard', badge: 'notifications' },
  ];

  // Grouped items
  const groups: (MenuGroup & { key: string })[] = [
    {
      key: 'operations',
      label: 'Bookings & Customers',
      icon: BookingsIcon,
      permission: 'manage_bookings',
      items: [
        { path: '/bookings', label: 'Bookings', permission: 'manage_bookings' },
        { path: '/customers', label: 'Customers', permission: 'view_dashboard' },
      ],
    },
    {
      key: 'fleet',
      label: 'Fleet Management',
      icon: FleetIcon,
      permission: 'manage_fleet',
      items: [
        { path: '/fleet', label: 'Vehicles', permission: 'manage_fleet' },
        { path: '/maintenance', label: 'Maintenance', permission: 'manage_fleet' },
        { path: '/issues', label: 'Issue Tracker', permission: 'manage_fleet' },
        { path: '/compliance', label: 'Compliance', permission: 'manage_fleet' },
      ],
    },
    {
      key: 'pricing',
      label: 'Pricing',
      icon: PricingIcon,
      permission: 'manage_discounts',
      items: [
        { path: '/price-management', label: 'Price Management', permission: 'manage_fleet' },
        { path: '/seasonal-pricing', label: 'Seasonal Pricing', permission: 'manage_discounts' },
        { path: '/discounts', label: 'Discounts', permission: 'manage_discounts' },
      ],
    },
    {
      key: 'content',
      label: 'Website Content',
      icon: WebsiteIcon,
      permission: 'view_dashboard',
      items: [
        { path: '/pages', label: 'Website CMS', permission: 'view_dashboard' },
        { path: '/blog', label: 'Blog Management', permission: 'view_dashboard' },
      ],
    },
    {
      key: 'analytics',
      label: 'Analytics & SEO',
      icon: AnalyticsIcon,
      permission: 'view_analytics',
      items: [
        { path: '/website-analytics', label: 'Website Analytics', permission: 'view_dashboard' },
        { path: '/seo', label: 'SEO Dashboard', permission: 'view_dashboard' },
      ],
    },
  ];

  // Bottom items
  const bottomItems: TopLevelItem[] = [
    { path: '/system-health', label: 'System Health', icon: HealthIcon, permission: 'view_dashboard' },
    { path: '/users', label: 'User Management', icon: UsersIcon, permission: 'manage_users' },
    { path: '/audit-logs', label: 'Audit Logs', icon: AuditIcon, permission: 'view_audit_logs', requiredRole: 'super_admin' },
  ];

  const isActive = (path: string) => location.pathname === path;
  const isGroupActive = (items: MenuItem[]) => items.some(item => location.pathname === item.path);

  const renderLink = (path: string, label: string, indented: boolean = false) => {
    const active = isActive(path);
    return (
      <Link
        key={path}
        to={path}
        className={`group flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 ${
          indented ? 'ml-7' : ''
        } ${
          active
            ? 'bg-gradient-to-r from-blue-600 to-blue-500 text-white shadow-lg shadow-blue-500/30'
            : 'text-gray-300 hover:bg-gray-800/50 hover:text-white'
        }`}
      >
        <span className="flex-1">{label}</span>
        {active && (
          <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
        )}
      </Link>
    );
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-20 lg:hidden"
          onClick={onToggle}
        />
      )}
      
      {/* Sidebar */}
      <div
        className={`fixed left-0 top-0 h-screen bg-gradient-to-b from-gray-900 via-gray-900 to-gray-800 text-white z-30 transform transition-transform duration-300 ease-in-out shadow-2xl flex flex-col ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0 lg:static lg:z-auto w-64`}
      >
        {/* Header */}
        <div className="flex-shrink-0 flex items-center justify-between p-6 border-b border-gray-700/50">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center shadow-lg overflow-hidden">
              <img src="/favicon.png" alt="Tak8" className="w-10 h-10 object-contain" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-orange-400">
                Tak8
              </h1>
              <p className="text-xs text-gray-400">Admin Panel</p>
            </div>
          </div>
          <button
            onClick={onToggle}
            className="lg:hidden text-gray-400 hover:text-white transition-colors p-1 hover:bg-gray-800 rounded"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        {/* Navigation */}
        <nav className="flex-1 mt-4 px-3 space-y-1 overflow-y-auto sidebar-scroll">
          {/* Top-level items */}
          {topItems.filter(item => hasPermission(item.permission)).map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`group flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 ${
                  active
                    ? 'bg-gradient-to-r from-blue-600 to-blue-500 text-white shadow-lg shadow-blue-500/30'
                    : 'text-gray-300 hover:bg-gray-800/50 hover:text-white'
                }`}
              >
                <div className="mr-3">
                  <Icon />
                </div>
                <span className="flex-1">{item.label}</span>
                {item.badge === 'notifications' && unreadCount > 0 && (
                  <span className="ml-2 bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full min-w-[20px] text-center">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
                {active && (
                  <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                )}
              </Link>
            );
          })}

          {/* Separator */}
          <div className="pt-3 pb-2">
            <div className="border-t border-gray-700/50"></div>
          </div>

          {/* Grouped items */}
          {groups.filter(group => hasPermission(group.permission)).map((group) => {
            const Icon = group.icon;
            const isExpanded = expandedGroups[group.key] || false;
            const groupActive = isGroupActive(group.items);

            return (
              <div
                key={group.key}
                onMouseEnter={(e) => handleGroupMouseEnter(group.key, e)}
                onMouseLeave={handleGroupMouseLeave}
              >
                <button
                  onClick={() => {
                    // Only expand on click for mobile (no hover available)
                    if (window.innerWidth < 1024) {
                      toggleGroup(group.key);
                    }
                  }}
                  className={`w-full group flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 ${
                    groupActive && !isExpanded
                      ? 'bg-blue-600/20 text-blue-300'
                      : 'text-gray-300 hover:bg-gray-800/50 hover:text-white'
                  }`}
                >
                  <div className="mr-3">
                    <Icon />
                  </div>
                  <span className="flex-1 text-left">{group.label}</span>
                  <span className="lg:hidden"><ChevronIcon open={isExpanded} /></span>
                </button>

                {/* Expandable sub-items (mobile only) */}
                <div className={`lg:hidden overflow-hidden transition-all duration-200 ${isExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
                  <div className="mt-1 space-y-0.5">
                    {group.items.filter(item => hasPermission(item.permission)).map((item) => 
                      renderLink(item.path, item.label, true)
                    )}
                  </div>
                </div>
              </div>
            );
          })}

          {/* Separator */}
          <div className="pt-3 pb-2">
            <div className="border-t border-gray-700/50"></div>
          </div>

          {/* Bottom items */}
          {bottomItems.filter(item => hasPermission(item.permission) && (!item.requiredRole || hasRole(item.requiredRole))).map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`group flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 ${
                  active
                    ? 'bg-gradient-to-r from-blue-600 to-blue-500 text-white shadow-lg shadow-blue-500/30'
                    : 'text-gray-300 hover:bg-gray-800/50 hover:text-white'
                }`}
              >
                <div className="mr-3">
                  <Icon />
                </div>
                <span className="flex-1">{item.label}</span>
                {active && (
                  <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                )}
              </Link>
            );
          })}
        </nav>

        {/* Footer */}
        <div className="flex-shrink-0 p-4 border-t border-gray-700/50 mt-auto">
          <div className="bg-gray-800/50 rounded-lg p-3">
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <span className="text-xs text-gray-400">System Online</span>
            </div>
            <p className="text-xs text-gray-500 mt-1">v1.0.0</p>
          </div>
        </div>
      </div>

      {/* Hover Flyout Panel - rendered outside sidebar to escape transform stacking context */}
      {hoveredGroup && flyoutPosition && (
        <div
          className="hidden lg:block fixed"
          style={{ left: '260px', top: `${flyoutPosition.top}px`, zIndex: 9999 }}
          onMouseEnter={handleFlyoutMouseEnter}
          onMouseLeave={handleFlyoutMouseLeave}
        >
          {/* Arrow pointing to sidebar */}
          <div className="absolute left-0 top-4 -translate-x-full">
            <div className="w-0 h-0 border-t-[8px] border-t-transparent border-b-[8px] border-b-transparent border-r-[8px] border-r-gray-700"></div>
          </div>

          <div className="bg-gray-800/95 backdrop-blur-md border border-gray-700 rounded-xl shadow-2xl py-3 px-2 min-w-[220px] animate-slideIn">
            {(() => {
              const group = groups.find(g => g.key === hoveredGroup);
              if (!group) return null;
              
              // Accent colors per group
              const accentColors: Record<string, { header: string; activeBg: string; dot: string }> = {
                operations: { header: 'text-blue-400', activeBg: 'bg-blue-600', dot: 'bg-blue-400' },
                fleet: { header: 'text-green-400', activeBg: 'bg-green-600', dot: 'bg-green-400' },
                pricing: { header: 'text-purple-400', activeBg: 'bg-purple-600', dot: 'bg-purple-400' },
                content: { header: 'text-cyan-400', activeBg: 'bg-cyan-600', dot: 'bg-cyan-400' },
                analytics: { header: 'text-orange-400', activeBg: 'bg-orange-600', dot: 'bg-orange-400' },
              };

              // Icons per path
              const itemIcons: Record<string, React.ReactNode> = {
                '/bookings': <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>,
                '/customers': <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" /></svg>,
                '/fleet': <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" /></svg>,
                '/maintenance': <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>,
                '/issues': <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>,
                '/compliance': <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>,
                '/price-management': <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>,
                '/seasonal-pricing': <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" /></svg>,
                '/discounts': <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" /></svg>,
                '/pages': <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" /></svg>,
                '/blog': <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>,
                '/website-analytics': <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>,
                '/seo': <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>,
              };

              const colors = accentColors[group.key] || accentColors.operations;

              return (
                <>
                  <div className={`px-3 py-2 flex items-center gap-2 border-b border-gray-700/50 mb-2`}>
                    <div className={`w-2 h-2 rounded-full ${colors.dot}`}></div>
                    <p className={`text-xs font-semibold uppercase tracking-wider ${colors.header}`}>{group.label}</p>
                  </div>
                  <div className="space-y-0.5">
                    {group.items.filter(item => hasPermission(item.permission)).map((item) => {
                      const active = isActive(item.path);
                      return (
                        <Link
                          key={item.path}
                          to={item.path}
                          onClick={() => { setHoveredGroup(null); setFlyoutPosition(null); }}
                          className={`flex items-center gap-3 px-3 py-2.5 text-sm rounded-lg transition-all duration-150 ${
                            active
                              ? `${colors.activeBg} text-white shadow-lg`
                              : 'text-gray-300 hover:bg-gray-700/70 hover:text-white'
                          }`}
                        >
                          <span className={`flex-shrink-0 ${active ? 'text-white' : 'text-gray-500'}`}>
                            {itemIcons[item.path] || <div className="w-4 h-4 rounded bg-gray-600"></div>}
                          </span>
                          <span className="flex-1">{item.label}</span>
                          {active && <div className={`w-2 h-2 rounded-full bg-white animate-pulse`}></div>}
                        </Link>
                      );
                    })}
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}
    </>
  );
};

export default Sidebar;
